import { LoginRequiredError } from '@workos-inc/authkit-react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import type { MeResponse } from '@noma-eco/platform/schema';
import { getMe, isNeedsReauthResponse } from '../api-clients/me-client.js';
import { AuthContext, type DevAuthContextValue } from './auth-context.js';
import {
  clearDevAuthToken,
  clearDevSessionExpired,
  readDevSessionExpired,
} from './dev-auth.js';
import { clearLastUsedOrgId } from './last-used-org-id.js';

// AuthKit keeps `user` populated after a failed refresh, so the expired
// simulation needs a user object without a /me round-trip (the token is
// rejecting). Contents are irrelevant — the dead-session surfaces render
// before anything reads it.
const EXPIRED_PLACEHOLDER_USER: MeResponse['user'] = {
  id: '00000000-0000-0000-0000-00000000dead',
  authSubject: 'user_dev_expired',
  email: 'expired@dev.invalid',
  firstName: null,
  lastName: null,
  displayName: 'Expired Dev Session',
  isInternalOrTest: true,
};

// Simulated hosted-round-trip duration before the "re-signed-in" reload —
// keeps the redirect state on screen long enough for Playwright to assert
// (and for a human to see) instead of an instant reload.
const SIMULATED_SIGN_IN_ROUNDTRIP_MS = 1_200;

export function DevAuthProvider({
  token,
  children,
}: {
  token: string;
  children: ReactNode;
}) {
  // Read once at mount — the flag only changes via a reload.
  const [sessionExpired] = useState(readDevSessionExpired);
  const [user, setUser] = useState<MeResponse['user'] | null>(
    sessionExpired ? EXPIRED_PLACEHOLDER_USER : null,
  );
  const [isLoading, setIsLoading] = useState(!sessionExpired);

  useEffect(() => {
    if (sessionExpired) return;
    let cancelled = false;
    void getMe()
      .then((response) => {
        if (cancelled) return;
        if (isNeedsReauthResponse(response)) {
          // Dev fixtures are pre-bootstrapped; reaching here means the fixture
          // user hasn't been provisioned yet. Drop the token so the developer
          // re-clicks and re-bootstraps.
          clearDevAuthToken();
          window.location.reload();
          return;
        }
        setUser(response.user);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionExpired]);

  const value: DevAuthContextValue = {
    user,
    isLoading,
    getAccessToken: sessionExpired
      ? () => Promise.reject(new LoginRequiredError())
      : () => Promise.resolve(token),
    signIn: sessionExpired
      ? () => {
          // Simulated successful hosted re-sign-in: clear the expiry flag
          // and reload after the fake round-trip delay.
          setTimeout(() => {
            clearDevSessionExpired();
            window.location.reload();
          }, SIMULATED_SIGN_IN_ROUNDTRIP_MS);
        }
      : () => {
          // Dev provider has no IdP — accepts the { organizationId? } option
          // for type compatibility with the AuthKit branch and ignores it.
        },
    signOut: () => {
      clearLastUsedOrgId();
      clearDevAuthToken();
      window.location.reload();
    },
    switchToOrganization: async () => {
      clearDevAuthToken();
      window.location.reload();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
